import React, { useCallback, useMemo, useState } from 'react';
import { HelperText, useTheme } from 'react-native-paper';
import { useMutation, useQueryClient } from 'react-query';
import type { ApplePayError, ConfirmPaymentError, GooglePayError, StripeError } from '@stripe/stripe-react-native';
import { CompositeScreenProps, useIsFocused } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { Alert } from 'react-native';
import { useI18n } from '../../context/I18nContext';
import { flattenDict } from '../../utils/helpers';
import {
  retrieveSubscriptionClientSecret,
  createSubscription,
  invalidateSubscriptionData,
  useGetProductById,
} from '../../apis/subscriptionApis';
import { PaymentMethodOption } from '../../types/misc.types';
import { useStripe } from '../../payment/utils/stripe';
import ConfirmationRow from '../../payment/components/ConfirmationRow';
import Container from '../../components/Container';
import { NavigationDialog } from '../CommonDialogs';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { CardPaymentMethod, useActivePayment } from '../../payment/MapViewActivePaymentContext';
import { trackMakeOneTimePayment } from '../../utils/mixpanelUtils';
import { useMixpanel } from '../../mixpanel/MixpanelContext';
import * as Sentry from '../../components/sentry/sentry';
import {
  Currency,
  Product,
  ProductPaymentType,
  ProductSubscription,
  ProductSubscriptionStatus,
  ServiceType,
} from '../../types/appsync-types';
import { AgreeToTermsRow } from '../../payment/components/AgreeToTermsRow';
import { screenNames } from '../../navigators/screenNames';
import { MapStackRouteParams } from '../../navigators/MapStackNavigator';
import { useParkingTermsUrlForCurrentLocale } from '../../hooks/useParkingTermsUrlForCurrentLocale';
import { useGetSpaceTerms } from '../../apis/mapApis';

export interface ConfirmOneTimePurchaseDialogRouteParams {
  id: string;
}

export type SubscriptionFailureHandler = (
  type:
    | 'PAYMENT'
    | 'CANCELLED'
    | 'STRIPE_SUBSCRIPTION_ID_MISSING'
    | 'PAYMENT_SECRET_MISSING'
    | 'UNEXPECTED'
    | 'UNEXPECTED_CONFIRMATION',
  data: ProductSubscription | undefined | null,
  stripeError?: StripeError<ConfirmPaymentError | GooglePayError | ApplePayError>,
) => void;

type Props = CompositeScreenProps<
  StackScreenProps<MapStackRouteParams, 'ConfirmOneTimePurchaseDialog'>,
  AppDrawerScreenProp
>;

export function ConfirmOneTimePurchaseDialog({ navigation, route }: Props) {
  const isScreenFocused = useIsFocused();
  const { activePaymentMethod, isActivePaymentMethodLoading } = useActivePayment();
  const { colors } = useTheme();
  const mp = useMixpanel();
  const queryClient = useQueryClient();
  const stripe = useStripe();
  const { I18n, formatCurrency } = useI18n();
  // const [useCredits, setUseCredits] = useState<boolean>(true);
  const [userAgreedToTerms, setUserAgreedToTerms] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // const myProfileQuery = useMyProfileQuery();
  // const creditBalance = myProfileQuery.data?.data?.getMyProfile?.creditBalance ?? undefined;
  // const creditCurrency = myProfileQuery.data?.data?.getMyProfile?.currency?.toLowerCase();

  const getProductByIdResult = useGetProductById(route.params.id);

  // TODO: Handle product being undefined
  const product = getProductByIdResult.data?.data?.getProductById;
  const subscription = product?.subscription;

  const isFreeProduct = product?.paymentType === ProductPaymentType.FREE;
  const isLockerProduct = product?.serviceType === ServiceType.LOCKER;

  // If this is a locker product, fetch locker group terms
  // TODO: Once we switch to using locker group products, let's get the terms from the product and not the locker group pod
  const lockerGroupTerms = useGetSpaceTerms(
    isLockerProduct ? product.linkedPods?.[0]?.parentRef ?? undefined : undefined,
  ).data?.data?.getPoisByID?.metadata?.parkingTerms;
  const termsUrl = useParkingTermsUrlForCurrentLocale(
    isLockerProduct && lockerGroupTerms ? lockerGroupTerms : product?.terms,
  );
  const isPaymentConfirmationFlow = subscription?.status === ProductSubscriptionStatus.REQUIRES_ACTION;

  // const [moneyToSpend, creditsToSpend] = useMemo(() => {
  //   if (product && product.price !== null && product.price !== undefined) {
  //     if (useCredits && creditCurrency === 'eur' && creditBalance) {
  //       const crd = Math.min(creditBalance, product.price);
  //       return [product.price - crd, crd];
  //     }
  //     return [product.price, 0];
  //   }
  //   return [undefined, undefined];
  // }, [creditBalance, creditCurrency, product, useCredits]);

  // TODO: Handle availability for products (can have multiple linked pods)
  // const isAvailable: boolean = useMemo(() => {
  //   const avail = podInformationQuery.data?.data?.getPoisByID?.availabilityStatus;
  //   if (avail && avail.status === PodStatus.FREE) {
  //     if (avail.nextFreeEnd) {
  //       const freeEndExclusive = new Date(avail.nextFreeEnd);
  //       const selectedEndTime = addSeconds(filter.date, filter.durationInSecond);
  //       return isAfterOrEqual(freeEndExclusive, selectedEndTime);
  //     }
  //     return true;
  //   }
  //   return false;
  // }, [filter.date, filter.durationInSecond, podInformationQuery.data?.data?.getPoisByID?.availabilityStatus]);
  const isAvailable = true;
  // const canUserPurchase = isAvailable && moneyToSpend !== undefined && creditsToSpend !== undefined;

  const handleUserDismissed = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onSubscriptionSucceeded = useCallback(
    (makeAProductSubscription: ProductSubscription, selectedProduct: Product) => {
      trackMakeOneTimePayment(mp, makeAProductSubscription, selectedProduct);

      // Sparing a bit time to try to eliminate the async update of subscription
      setTimeout(() => {
        invalidateSubscriptionData(queryClient);
      }, 1000);
      // TODO: Make sure, if possible, that user can't come back to this screen by navigating forward
      navigation.jumpTo('map', { screen: screenNames.Map });
    },
    [mp, navigation, queryClient],
  );

  const handleSubscriptionFailed: SubscriptionFailureHandler = useCallback(
    (type, makeAProductSubscription, stripeError) => {
      mp?.getPeople().increment('One-time payment failures', 1);
      if (type === 'CANCELLED') {
        mp?.track('One-time payment cancelled', flattenDict('subscription', makeAProductSubscription));
        setErrorMessage(
          `${I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.purchaseCancelled.title')}: ${I18n.t(
            'oneTimePurchase.confirmOneTimePurchaseModal.purchaseCancelled.description',
          )}`,
        );
      } else if (type === 'PAYMENT_SECRET_MISSING') {
        mp?.track(
          'One-time payment confirmation failed due to payment secret missing',
          flattenDict('subscription', makeAProductSubscription),
        );
        setErrorMessage(`${I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.purchaseFailMainContent')}`);
      } else if (type === 'STRIPE_SUBSCRIPTION_ID_MISSING') {
        mp?.track(
          'One-time payment confirmation failed due to Stripe subscription id missing',
          flattenDict('subscription', makeAProductSubscription),
        );
        setErrorMessage(`${I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.purchaseFailMainContent')}`);
      } else if (stripeError) {
        mp?.track('One-time payment failed', flattenDict('error', stripeError));
        setErrorMessage(stripeError.localizedMessage ?? stripeError.message);
      } else if (type === 'UNEXPECTED_CONFIRMATION') {
        mp?.track('One-time payment confirmation failed', flattenDict('subscription', makeAProductSubscription));
        setErrorMessage(`${I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.purchaseFailMainContent')}`);
      } else {
        mp?.track('One-time payment failed', flattenDict('subscription', makeAProductSubscription));
        setErrorMessage(`${I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.purchaseFailMainContent')}`);
      }
    },
    [mp, I18n],
  );

  const handleSubscriptionPaymentConfirmationWithCard = useCallback(
    async (paymentSecret: string, createdSubscription: ProductSubscription): Promise<void> => {
      try {
        const confirmPaymentResult = await stripe.confirmPayment(paymentSecret, {
          paymentMethodType: 'Card',
          paymentMethodData: { paymentMethodId: (activePaymentMethod as CardPaymentMethod).stripeCard.id },
        });
        if (confirmPaymentResult.paymentIntent) {
          // TODO: properly handle product being null | undefined
          onSubscriptionSucceeded(createdSubscription, product!);
        } else {
          throw confirmPaymentResult.error;
        }
      } catch (error) {
        console.error('Payment failed', error);
        mp?.getPeople().increment('Payment failures', 1);
        mp?.track('One-time payment failed', {
          ...flattenDict('subscription', createdSubscription),
          ...flattenDict('error', error),
        });
        Sentry.captureException(error);
        handleSubscriptionFailed('PAYMENT', createdSubscription, error as StripeError<ConfirmPaymentError>);
      }
    },
    [stripe, activePaymentMethod, onSubscriptionSucceeded, product, mp, handleSubscriptionFailed],
  );

  const retrieveSubscriptionClientSecretMutation = useMutation(
    async (createdSubscription: ProductSubscription) => {
      if (!createdSubscription.id) {
        // one time purchases dont have stripe subscription id
        throw new Error('No subscription id!');
      }
      return retrieveSubscriptionClientSecret({ subscriptionId: createdSubscription.id });
    },
    {
      async onSuccess(data, createdSubscription) {
        const paymentSecret = data?.data?.retrieveSubscriptionClientSecret;
        if (!paymentSecret) {
          return handleSubscriptionFailed('PAYMENT_SECRET_MISSING', createdSubscription);
        }

        // TODO: Possibly add separate function to handle confirmation with Google/Apple pay? (See ConfirmReservationDialog for reference)
        await handleSubscriptionPaymentConfirmationWithCard(paymentSecret, createdSubscription);
      },
      onError(error: any) {
        const errorCode = error?.errors?.[0]?.message;
        console.error(errorCode);
        handleSubscriptionFailed('UNEXPECTED_CONFIRMATION', null);
      },
    },
  );

  const handleSubscriptionWithCard = useCallback(
    async (createdSubscription: ProductSubscription): Promise<void> => {
      try {
        const confirmPaymentResult = { paymentIntent: true, error: null };
        // const confirmPaymentResult = await stripe.confirmPayment(createdSubscription?.paymentSecret!, {
        //   paymentMethodType: 'Card',
        //   paymentMethodData: { paymentMethodId: (activePaymentMethod as CardPaymentMethod).stripeCard.id },
        // });
        if (confirmPaymentResult.paymentIntent) {
          // TODO: properly handle product being null | undefined
          onSubscriptionSucceeded(createdSubscription, product!);
        } else {
          throw confirmPaymentResult.error;
        }
      } catch (error) {
        console.error('Payment failed', error);
        mp?.getPeople().increment('Payment failures', 1);
        mp?.track('One-time payment failed', {
          ...flattenDict('subscription', createdSubscription),
          ...flattenDict('error', error),
        });
        Sentry.captureException(error);
        handleSubscriptionFailed('PAYMENT', createdSubscription, error as StripeError<ConfirmPaymentError>);
      }
    },
    [onSubscriptionSucceeded, product, mp, handleSubscriptionFailed],
  );

  const handleSubscriptionAction = useCallback(
    async (productSubscription: ProductSubscription | undefined | null) => {
      switch (productSubscription?.status) {
        case ProductSubscriptionStatus.FAILED:
          handleSubscriptionFailed('PAYMENT', productSubscription);
          break;
        case ProductSubscriptionStatus.MISSING_PAYMENT:
          mp?.track('One-time payment pending', flattenDict('subscription', productSubscription));
          // activePaymentMethod can not be null, because user cannot click payment button without active payment method
          if (!activePaymentMethod) {
            Alert.alert(I18n.t('payment.alert.noActivePaymentMethod'));
            return;
          }
          if (activePaymentMethod.paymentMethodType === PaymentMethodOption.CARD) {
            await handleSubscriptionWithCard(productSubscription);
          }
          break;

        case ProductSubscriptionStatus.REQUIRES_ACTION:
          mp?.track('One-time payment requires action', flattenDict('subscription', productSubscription));
          await retrieveSubscriptionClientSecretMutation.mutateAsync(productSubscription);
          break;

        case ProductSubscriptionStatus.ACTIVE:
        case ProductSubscriptionStatus.ONE_TIME_PAID:
          // TODO: properly handle product being null | undefined
          onSubscriptionSucceeded(productSubscription, product!);
          break;
        default:
          handleSubscriptionFailed('UNEXPECTED', productSubscription);
          break;
      }
    },
    [
      I18n,
      activePaymentMethod,
      handleSubscriptionFailed,
      handleSubscriptionWithCard,
      mp,
      onSubscriptionSucceeded,
      product,
      retrieveSubscriptionClientSecretMutation,
    ],
  );

  const makeASubscriptionMutation = useMutation(
    async (input: Parameters<typeof createSubscription>) => {
      console.log('input', input);
      return createSubscription(...input);
    },
    {
      async onSuccess(data) {
        handleSubscriptionAction(data.data?.makeAProductSubscription);
      },
      onError(error: any) {
        /* Could not do pre-payment-booking */
        const errorCode = error?.errors?.[0]?.message;
        console.error(errorCode);
        handleSubscriptionFailed('UNEXPECTED', null);
      },
      onSettled() {
        // Sparing a bit time to try to eliminate the async update of subscription
        setTimeout(() => {
          invalidateSubscriptionData(queryClient);
        }, 1000);
      },
    },
  );

  const onConfirm = useCallback(
    async (confirmedCreditsToUse?: number | undefined) => {
      // const creditsAmount = confirmedCreditsToUse ?? 0; // TODO: Use credits?
      if (!product) throw new Error('Product is undefined!');

      if (!subscription && (isFreeProduct || activePaymentMethod?.paymentMethodType === PaymentMethodOption.CARD)) {
        // The actual logic of what to do after this, is in the mutations' success / fail handlers.
        await makeASubscriptionMutation.mutateAsync([
          {
            input: {
              productId: product.id,
              cardId: (activePaymentMethod as CardPaymentMethod | null)?.stripeCard.id,
            },
          },
        ]);
      } else if (isPaymentConfirmationFlow) {
        handleSubscriptionAction(subscription);
      } else {
        setErrorMessage(`${I18n.t('payment.alert.noActivePaymentMethod')}`);
      }
    },
    [
      product,
      subscription,
      isFreeProduct,
      activePaymentMethod,
      isPaymentConfirmationFlow,
      makeASubscriptionMutation,
      handleSubscriptionAction,
      I18n,
    ],
  );

  const userCanPay = useMemo(() => {
    if (isFreeProduct) {
      return true;
    }
    if (isActivePaymentMethodLoading || makeASubscriptionMutation.isLoading) {
      return undefined;
    }
    if (activePaymentMethod) {
      return true;
    }
    // if (moneyToSpend === 0) {
    //   return true;
    // }
    return false;
  }, [activePaymentMethod, isActivePaymentMethodLoading, isFreeProduct, makeASubscriptionMutation.isLoading]);

  if (!isScreenFocused) {
    return null;
  }

  let title = subscription
    ? I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.paymentRetryTitle')
    : I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.title');
  if (isFreeProduct) title = I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.freeTitle');

  return (
    <NavigationDialog
      style={{ backgroundColor: colors.elevation.level2 }} // TODO: is this correct?
      title={title}
      description={''}
      onDismiss={handleUserDismissed}
      actions={[
        {
          title: I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.cancel'),
          onPress: handleUserDismissed,
          disable: makeASubscriptionMutation.isLoading || retrieveSubscriptionClientSecretMutation.isLoading,
        },
        {
          title: I18n.t('oneTimePurchase.confirmOneTimePurchaseModal.confirm'),
          loading:
            makeASubscriptionMutation.isLoading ||
            isActivePaymentMethodLoading ||
            getProductByIdResult.isLoading ||
            retrieveSubscriptionClientSecretMutation.isLoading,
          disable:
            !product ||
            !userCanPay ||
            !isAvailable ||
            (!userAgreedToTerms && !subscription) ||
            (!!subscription && !isPaymentConfirmationFlow),
          onPress: () => {
            return onConfirm();
          },
        },
      ]}
      iconName={'check'}
    >
      {/* Product */}
      <ConfirmationRow title={product?.name ?? '?'} iconName="clock" />

      <ConfirmationRow
        iconName="wallet"
        title={isFreeProduct ? I18n.t('oneTimePurchase.detail.free') : formatCurrency(product?.price, Currency.EUR)}
      />

      {/* <UseCreditsRow
        balance={creditBalance}
        currency={creditCurrency}
        disabled={creditCurrency !== 'eur'}
        value={useCredits}
        onValueChange={setUseCredits}
        errorText={creditCurrency !== 'eur' ? I18n.t('mapview.modalConfirmation.creditCurrencyNotMatching') : undefined}
      />

      <AmountDueRow
        canUserMakeBooking={canUserPurchase}
        moneyToSpend={moneyToSpend}
        currency={'EUR'}
        securityDeposit={0}
      /> */}

      {!subscription && (
        <AgreeToTermsRow
          style={{ marginTop: 24 }}
          text={I18n.t((isLockerProduct ? 'lockerInfo.' : '') + 'oneTimePurchase.confirmOneTimePurchaseModal.terms')}
          onValueChange={setUserAgreedToTerms}
          onTermsClick={() => {
            // TODO: What to do if terms aren't defined?
            if (termsUrl) {
              navigation.navigate(screenNames.ParkingTerms, {
                url: termsUrl,
              });
            }
          }}
          value={userAgreedToTerms}
        />
      )}

      {/* Error text */}
      <Container>
        <HelperText type={'error'} visible={!!errorMessage}>
          {errorMessage}
        </HelperText>
      </Container>
    </NavigationDialog>
  );
}
